<template>
  <div
    ref="folderTreeSelect"
    class="folder-tree-select"
  >
    <div
      ref="trigger"
      class="folder-tree-select__trigger"
      :class="{
        'folder-tree-select__trigger--selected': hasSelection,
        'folder-tree-select__trigger--opened': isVisible,
      }"
      role="button"
      tabindex="0"
      @click="handleToggleDropdown"
      @keydown.enter.prevent="handleToggleDropdown"
      @keydown.space.prevent="handleToggleDropdown"
      @keydown.esc="closeDropdown"
    >
      <b-icon icon="folder" />
      <span class="folder-tree-select__trigger-label">{{ triggerLabel }}</span>
      <button
        v-if="hasSelection"
        type="button"
        class="folder-tree-select__remove"
        aria-label="Remove folder selection"
        @click.stop="handleRemove"
      >
        <b-icon icon="x" />
      </button>
      <b-icon
        icon="chevron-down"
        class="folder-tree-select__chevron"
      />
    </div>

    <div
      ref="dropdown"
      class="folder-tree-select__dropdown"
      :class="{ 'folder-tree-select__dropdown--opened': isVisible }"
      @mousedown.stop
      @click.stop
    >
      <div class="folder-tree-select__search">
        <b-icon icon="search" />
        <b-form-input
          ref="searchInput"
          v-model="searchValue"
          placeholder="Search folders..."
          autocomplete="off"
          @input="handleSearchFolder"
          @keydown.esc="closeDropdown"
        />
      </div>

      <div class="folder-tree-select__root-label">
        <b-icon icon="house" />
        <span>Files</span>
      </div>

      <div class="folder-tree-select__list">
        <template v-if="!folders">
          <div class="folder-tree-select__status">
            <span
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          </div>
        </template>
        <template v-else-if="folders.length">
          <FolderTreeSelectItem
            v-for="folder in folders"
            :key="folder.id"
            :item="folder"
            :selected="pendingFolder"
            @change="handleFolderChange"
          />
        </template>
        <template v-else>
          <div class="folder-tree-select__status">
            No folders found.
          </div>
        </template>
      </div>

      <div
        v-if="isCreatingFolder"
        class="folder-tree-select__new-folder-form"
      >
        <input
          ref="newFolderInput"
          v-model="draftFolderName"
          type="text"
          class="form-control folder-tree-select__new-folder-input"
          placeholder="New folder name"
          autocomplete="off"
          @mousedown.stop
          @click.stop
          @keydown.enter.stop.prevent="createFolder"
          @keydown.esc.stop.prevent="cancelCreateFolder"
        >
        <button
          type="button"
          class="folder-tree-select__create-button"
          :disabled="!draftFolderName.trim()"
          @click="createFolder"
        >
          Create
        </button>
        <button
          type="button"
          class="folder-tree-select__cancel-button"
          @click="cancelCreateFolder"
        >
          Cancel
        </button>
      </div>
      <div
        v-else
        class="folder-tree-select__footer"
      >
        <button
          type="button"
          class="folder-tree-select__new-folder-button"
          @click.stop="startCreateFolder"
        >
          <b-icon icon="folder-plus" />
          <span>New folder</span>
        </button>
        <button
          type="button"
          class="folder-tree-select__use-button"
          :disabled="!pendingFolder"
          @click="useSelectedFolder"
        >
          Use this folder
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FolderTreeSelectItem from "@frontend/components/common/atoms/FolderTreeSelect/FolderTreeSelectItem.vue";

const INPUT_SEARCH_PLACEHOLDER = 'Select option'

export default {
  name: 'FolderTreeSelect',
  components: { FolderTreeSelectItem },
  props: {
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    value: {
      type: Object,
      default: null
    },
    placeholder: {
      type: String,
      default: INPUT_SEARCH_PLACEHOLDER
    },
    newFolderName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      selectedFolder: this.value,
      pendingFolder: this.value,
      isVisible: false,
      isCreatingFolder: false,
      searchValue: '',
      draftFolderName: '',
      folders: this.items
    }
  },

  computed: {
    hasSelection() {
      return Boolean(this.selectedFolder || this.newFolderName);
    },
    triggerLabel() {
      return this.newFolderName || this.selectedFolder?.name || this.placeholder;
    }
  },

  watch: {
    items(newItems) {
      this.folders = newItems;
    },
    value(newValue) {
      this.selectedFolder = newValue;
      this.pendingFolder = newValue;
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    handleFolderChange(folder) {
      this.pendingFolder = folder;
    },
    handleClickOutside(event) {
      const clickedTrigger = this.$refs.folderTreeSelect?.contains(event.target);
      const clickedDropdown = this.$refs.dropdown?.contains(event.target);

      if (!clickedTrigger && !clickedDropdown) {
        this.closeDropdown();
      }
    },
    handleOpenDropdown() {
      if (this.isVisible) return;

      this.isVisible = true;
      this.isCreatingFolder = false;
      this.searchValue = '';
      this.draftFolderName = '';
      this.pendingFolder = this.selectedFolder;
      this.folders = this.items;
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },
    handleToggleDropdown() {
      if (this.isVisible) {
        this.closeDropdown();
      } else {
        this.handleOpenDropdown();
      }
    },
    closeDropdown() {
      this.isVisible = false;
      this.isCreatingFolder = false;
      this.pendingFolder = this.selectedFolder;
    },
    handleSearchFolder(value = '') {
      const normalizedValue = value.trim().toLowerCase();
      this.folders = this.items.filter(item => item.name.toLowerCase().includes(normalizedValue));
    },
    useSelectedFolder() {
      if (!this.pendingFolder) return;

      this.selectedFolder = this.pendingFolder;
      this.$emit('clear-new-folder');
      this.$emit('input', this.selectedFolder);
      this.closeDropdown();
    },
    handleRemove() {
      this.selectedFolder = null;
      this.pendingFolder = null;
      this.$emit('clear-new-folder');
      this.$emit('input', null);
    },
    startCreateFolder() {
      this.isVisible = true;
      this.isCreatingFolder = true;
      this.draftFolderName = '';
      this.$nextTick(() => {
        const input = this.$refs.newFolderInput;
        if (input) {
          input.focus();
          input.setSelectionRange(input.value.length, input.value.length);
        }
      });
    },
    cancelCreateFolder() {
      this.isCreatingFolder = false;
      this.draftFolderName = '';
    },
    createFolder() {
      const name = this.draftFolderName.trim();
      if (!name) return;

      this.selectedFolder = null;
      this.pendingFolder = null;
      this.$emit('input', null);
      this.$emit('create-folder', name);
      this.closeDropdown();
    }
  }
}
</script>

<style lang="scss" scoped>
.folder-tree-select {
  position: relative;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
    padding: 8px 12px;
    border: 1px solid #dfe5ed;
    border-radius: 4px;
    color: #172033;
    background: #fff;
    cursor: pointer;
    outline: none;
    font-size: .875rem;
    line-height: 1.25rem;
    font-weight: 400;

    &:focus,
    &--opened {
      border-color: #209c86;
      box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
    }

    &:hover {
      border-color: #19a17f;
      color: #fff;
      background: #19a17f;
    }

    &--selected {
      border-color: #209c86;
      color: #fff;
      background: #4ba183;
    }
  }

  &__trigger-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    color: inherit;
    background: transparent;
    font-size: 18px;
  }

  &__chevron {
    font-size: 12px;
    transition: transform 0.2s ease;
  }

  &__trigger--opened &__chevron {
    transform: rotate(180deg);
  }

  &__dropdown {
    display: none;
    position: absolute;
    right: 0;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 2000;
    border: 1px solid #dfe5ed;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
    pointer-events: auto;

    &--opened {
      display: block;
    }
  }

  &__search {
    position: relative;
    padding: 6px;

    > svg {
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 18px;
      transform: translateY(-50%);
      color: #64748b;
    }

    input {
      min-height: 38px;
      padding-left: 36px;
      border-color: #209c86;
      cursor: text;
      box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
    }
  }

  &__root-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border-top: 1px solid #e5eaf0;
    border-bottom: 1px solid #e5eaf0;
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
  }

  &__list {
    max-height: 255px;
    padding: 5px 0;
    overflow-y: auto;
  }

  &__status {
    padding: 10px 14px;
    color: #64748b;
    font-size: 13px;
  }

  &__footer,
  &__new-folder-form {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-top: 1px solid #e5eaf0;
  }

  &__new-folder-button,
  &__use-button,
  &__create-button,
  &__cancel-button {
    border: 0;
    background: transparent;
    font-size: 13px;
    font-weight: 600;
  }

  &__new-folder-button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    padding: 7px 12px;
    border-radius: 4px;
    color: #fff;
    background: #209c86;

    &:hover,
    &:focus {
      color: #fff;
      background: #178b76;
    }
  }

  &__use-button {
    margin-left: auto;
    color: #64748b;

    &:not(:disabled) {
      color: #209c86;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  &__new-folder-form {
    input {
      min-width: 0;
      min-height: 38px;
      border-color: #209c86;
      box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
      caret-color: #172033;
      cursor: text !important;
      pointer-events: auto;
      user-select: text;
    }
  }

  &__create-button {
    min-height: 36px;
    padding: 7px 12px;
    border-radius: 4px;
    color: #fff;
    background: #4ba183;

    &:disabled {
      opacity: 0.55;
    }
  }

  &__cancel-button {
    padding: 7px 4px;
    color: #64748b;
  }
}
</style>
